import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Save, Loader2, Settings, Mail, Bell, Download, Globe, Calendar,
  Building, Clock, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('site');
  const [showApiKey, setShowApiKey] = useState(false);
  const [settings, setSettings] = useState({
    site: {
      site_name: 'World Nuclear Congress',
      contact_email: 'contact@ceeleaders.com',
      event_name: 'World Nuclear Congress',
      event_dates: 'Coming February 2027',
      event_location: 'University of Tennessee, Knoxville',
      timezone: 'America/New_York'
    },
    email: {
      sendgrid_api_key: '',
      default_sender_name: 'World Nuclear Congress',
      default_sender_email: 'noreply@usceef.org',
      reply_to_email: 'contact@ceeleaders.com',
      footer_text: '© 2027 World Nuclear Congress. All rights reserved.'
    },
    notifications: {
      email_on_new_lead: true,
      email_on_golf_inquiry: true,
      daily_summary: false,
      notification_email: 'contact@ceeleaders.com'
    }
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          site: { ...prev.site, ...data.site },
          email: { ...prev.email, ...data.email },
          notifications: { ...prev.notifications, ...data.notifications }
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/export/${type}`);
      if (response.ok) {
        const data = await response.json();
        const items = type === 'leads' ? data.leads : data.inquiries;
        
        if (items.length === 0) {
          toast.info(`No ${type} to export`);
          return;
        }

        // Convert to CSV
        const headers = Object.keys(items[0]);
        const csv = [
          headers.join(','),
          ...items.map(item => 
            headers.map(h => {
              const val = item[h];
              if (val === null || val === undefined) return '';
              if (typeof val === 'object') return JSON.stringify(val);
              return `"${String(val).replace(/"/g, '""')}"`;
            }).join(',')
          )
        ].join('\n');

        // Download
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.count} ${type}`);
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const tabs = [
    { id: 'site', label: 'Site Settings', icon: Globe },
    { id: 'email', label: 'Email Settings', icon: Mail },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'export', label: 'Export Data', icon: Download }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600">Configure your application settings</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00457f] hover:bg-[#003461]">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#00457f] border-b-2 border-[#00457f]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Site Settings Tab */}
        {activeTab === 'site' && (
          <Card>
            <CardHeader>
              <CardTitle>Site Settings</CardTitle>
              <CardDescription>General configuration for your website</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Site Name</Label>
                  <Input
                    value={settings.site.site_name}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      site: { ...prev.site, site_name: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={settings.site.contact_email}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      site: { ...prev.site, contact_email: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label>Event Name</Label>
                  <Input
                    value={settings.site.event_name}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      site: { ...prev.site, event_name: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label>Event Dates</Label>
                  <Input
                    value={settings.site.event_dates}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      site: { ...prev.site, event_dates: e.target.value }
                    }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Event Location</Label>
                  <Input
                    value={settings.site.event_location}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      site: { ...prev.site, event_location: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label>Timezone</Label>
                  <select
                    value={settings.site.timezone}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      site: { ...prev.site, timezone: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email' && (
          <Card>
            <CardHeader>
              <CardTitle>Email Settings</CardTitle>
              <CardDescription>Configure email sending options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> SendGrid API key is required for sending email campaigns. 
                  Get your API key from <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="underline">sendgrid.com</a>
                </p>
              </div>
              
              <div>
                <Label>SendGrid API Key</Label>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={settings.email.sendgrid_api_key || ''}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, sendgrid_api_key: e.target.value }
                    }))}
                    placeholder="SG.xxxxxxxx..."
                    className="font-mono"
                  />
                  <Button
                    variant="outline"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Default Sender Name</Label>
                  <Input
                    value={settings.email.default_sender_name}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, default_sender_name: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label>Default Sender Email</Label>
                  <Input
                    type="email"
                    value={settings.email.default_sender_email}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, default_sender_email: e.target.value }
                    }))}
                  />
                </div>
                <div>
                  <Label>Reply-To Email</Label>
                  <Input
                    type="email"
                    value={settings.email.reply_to_email}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, reply_to_email: e.target.value }
                    }))}
                  />
                </div>
              </div>

              <div>
                <Label>Email Footer Text</Label>
                <Input
                  value={settings.email.footer_text}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    email: { ...prev.email, footer_text: e.target.value }
                  }))}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Configure when you receive email notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Notification Email</Label>
                <Input
                  type="email"
                  value={settings.notifications.notification_email}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    notifications: { ...prev.notifications, notification_email: e.target.value }
                  }))}
                  placeholder="Where to send notifications"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">New Lead Notification</p>
                    <p className="text-sm text-gray-500">Receive an email when someone registers</p>
                  </div>
                  <Switch
                    checked={settings.notifications.email_on_new_lead}
                    onCheckedChange={(checked) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, email_on_new_lead: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Golf Inquiry Notification</p>
                    <p className="text-sm text-gray-500">Receive an email for golf outing interest forms</p>
                  </div>
                  <Switch
                    checked={settings.notifications.email_on_golf_inquiry}
                    onCheckedChange={(checked) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, email_on_golf_inquiry: checked }
                    }))}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Daily Summary</p>
                    <p className="text-sm text-gray-500">Receive a daily summary of all activity</p>
                  </div>
                  <Switch
                    checked={settings.notifications.daily_summary}
                    onCheckedChange={(checked) => setSettings(prev => ({
                      ...prev,
                      notifications: { ...prev.notifications, daily_summary: checked }
                    }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <Card>
            <CardHeader>
              <CardTitle>Export Data</CardTitle>
              <CardDescription>Download your data as CSV files</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-6 border rounded-lg hover:border-[#00457f] transition-colors">
                  <h3 className="font-bold text-gray-900 mb-2">Lead Registrations</h3>
                  <p className="text-sm text-gray-500 mb-4">Export all registration form submissions</p>
                  <Button onClick={() => handleExport('leads')} variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Export Leads CSV
                  </Button>
                </div>

                <div className="p-6 border rounded-lg hover:border-[#00457f] transition-colors">
                  <h3 className="font-bold text-gray-900 mb-2">Golf Inquiries</h3>
                  <p className="text-sm text-gray-500 mb-4">Export all golf outing interest submissions</p>
                  <Button onClick={() => handleExport('golf')} variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Export Golf CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
